if (!customElements.get('pm-platform-marquee')) {
  const INTERACTIVE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex], [contenteditable]';
  // PM 05-10-2026: arrastre con mouse / deslizamiento táctil.
  const DRAG_THRESHOLD = 6; // px antes de considerar que es arrastre (y no clic/toque)
  const INERTIA_DECAY = 0.95; // por cada ~16 ms
  const INERTIA_MIN = 0.02; // px/ms bajo el cual la inercia se detiene
  // PM 27-10-2026 (INP / PageSpeed): el movimiento continuo ya no lo hace un
  // bucle requestAnimationFrame en el hilo principal (60 veces por segundo),
  // sino una animación Web Animations API sobre transform, que el navegador
  // corre en el hilo de composición. JavaScript solo trabaja mientras el
  // usuario arrastra y durante la inercia, moviendo la MISMA animación con
  // currentTime (sin estilos en línea). Las mediciones que disparan los
  // logos al cargar (PM-DEFER) se agrupan, y la cinta solo se re-clona si
  // cambia su ancho real. Comportamiento visible igual que antes.
  const MEASURE_DELAY = 120; // ms para agrupar los ResizeObserver de los logos

  function stripEditorArtifacts(root) {
    const nodes = [root, ...root.querySelectorAll('*')];
    nodes.forEach((el) => {
      if (el.hasAttribute('id')) el.removeAttribute('id');
      Array.from(el.attributes).forEach((attr) => {
        if (attr.name.indexOf('data-shopify-') === 0) {
          el.removeAttribute(attr.name);
        }
      });
    });
  }

  // Normaliza el desplazamiento al rango (-g, 0] para el bucle infinito.
  function wrapOffset(x, g) {
    if (!g) return 0;
    let r = x % g;
    if (r > 0) r -= g;
    return r;
  }

  class PmPlatformMarquee extends HTMLElement {
    connectedCallback() {
      if (this._resizeObserver) return;

      this.viewport = this.querySelector('.pm-platform-marquee__viewport');
      this.track = this.querySelector('.pm-platform-marquee__track');
      if (!this.viewport || !this.track) return;

      this.baseGroup = this.track.querySelector('.pm-platform-marquee__group');
      if (!this.baseGroup) return;

      this._measureTimer = null;
      this._focusRafId = null;

      this._anim = null;
      this._duration = 0;
      this._groupWidth = 0;
      this._containerWidth = 0;
      this._offset = 0;
      this._inertiaId = null;
      this._lastTime = 0;
      this._hovering = false;
      this._inView = true;
      this._velocity = 0;
      this._pointer = null;
      this._suppressClick = false;

      this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._onMotionChange = () => {
        if (this._motionQuery.matches) {
          this.cancelMeasure();
          this.disableJsMotion();
          this.removeDynamicClones();
        } else {
          this.enableJsMotion();
          this.scheduleMeasure();
        }
      };
      this._motionQuery.addEventListener('change', this._onMotionChange);

      this._resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this._resizeObserver.observe(this);
      this._resizeObserver.observe(this.baseGroup);

      this._onFocusIn = (event) => this.handleFocusIn(event);
      this._onFocusOut = (event) => this.handleFocusOut(event);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);

      if (!this._motionQuery.matches) this.enableJsMotion();
    }

    disconnectedCallback() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._motionQuery && this._onMotionChange) {
        this._motionQuery.removeEventListener('change', this._onMotionChange);
      }
      this._motionQuery = null;
      this._onMotionChange = null;

      if (this._onFocusIn) this.removeEventListener('focusin', this._onFocusIn);
      if (this._onFocusOut) this.removeEventListener('focusout', this._onFocusOut);
      this._onFocusIn = null;
      this._onFocusOut = null;

      this.cancelMeasure();
      if (this._focusRafId) {
        window.cancelAnimationFrame(this._focusRafId);
        this._focusRafId = null;
      }

      this.disableJsMotion();

      this.classList.remove('pm-platform-marquee--focus-active');
      if (this.viewport) this.viewport.scrollLeft = 0;

      this.removeDynamicClones();
    }

    /* ---------- Animación (hilo de composición) ---------- */

    direction() {
      return this.dataset.direction === 'right' ? 1 : -1;
    }

    // Posición actual (-g, 0] según el tiempo de la animación.
    currentOffset() {
      const g = this._groupWidth;
      if (!this._anim || !g || !this._duration) return this._offset;
      const t = ((Number(this._anim.currentTime) || 0) % this._duration) / this._duration;
      return this.direction() < 0 ? -g * t : -g + g * t;
    }

    // Lleva la animación a una posición dada (sin estilos en línea).
    seekToOffset(offset) {
      const g = this._groupWidth;
      if (!this._anim || !g || !this._duration) return;
      const o = wrapOffset(offset, g);
      const t = this.direction() < 0 ? -o / g : (o + g) / g;
      this._anim.currentTime = (t % 1) * this._duration;
    }

    buildAnimation() {
      const g = this._groupWidth;
      if (!this._jsEnabled || !g) return;
      const speed = parseFloat(this.dataset.speed) || 80; // px/s
      const previous = this._anim;
      const ratio = previous && this._duration
        ? ((Number(previous.currentTime) || 0) % this._duration) / this._duration
        : 0;
      if (previous) previous.cancel();

      const from = this.direction() < 0 ? 0 : -g;
      const to = this.direction() < 0 ? -g : 0;
      this._duration = (g / speed) * 1000;
      this._anim = this.track.animate(
        [
          { transform: `translate3d(${from}px, 0, 0)` },
          { transform: `translate3d(${to}px, 0, 0)` },
        ],
        { duration: this._duration, iterations: Infinity, easing: 'linear' }
      );
      this._anim.currentTime = ratio * this._duration;
      this.updatePlayState();
    }

    shouldPlay() {
      if (!this._jsEnabled || !this._anim) return false;
      if (!this._inView) return false;
      if (this._pointer && this._pointer.dragging) return false;
      if (this._inertiaId) return false;
      if (this.classList.contains('pm-platform-marquee--focus-active')) return false;
      if (this._hovering && this.classList.contains('pm-platform-marquee--pause-hover')) return false;
      return true;
    }

    updatePlayState() {
      if (!this._anim) return;
      if (this.shouldPlay()) {
        if (this._anim.playState !== 'running') this._anim.play();
      } else if (this._anim.playState === 'running') {
        this._anim.pause();
      }
    }

    /* ---------- Arrastre e inercia (JS solo mientras se usa) ---------- */

    enableJsMotion() {
      if (this._jsEnabled) return;
      this._jsEnabled = true;
      this.classList.add('pm-platform-marquee--js');

      this._onPointerDown = (e) => this.handlePointerDown(e);
      this._onPointerMove = (e) => this.handlePointerMove(e);
      this._onPointerUp = (e) => this.handlePointerUp(e, false);
      this._onPointerCancel = (e) => this.handlePointerUp(e, true);
      this._onPointerEnter = (e) => {
        if (e.pointerType !== 'mouse') return;
        this._hovering = true;
        this.updatePlayState();
      };
      this._onPointerLeave = (e) => {
        if (e.pointerType !== 'mouse') return;
        this._hovering = false;
        this.updatePlayState();
      };
      this._onClickCapture = (e) => {
        if (!this._suppressClick) return;
        this._suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      };
      this._onDragStart = (e) => e.preventDefault();

      this.viewport.addEventListener('pointerdown', this._onPointerDown);
      this.viewport.addEventListener('pointermove', this._onPointerMove);
      this.viewport.addEventListener('pointerup', this._onPointerUp);
      this.viewport.addEventListener('pointercancel', this._onPointerCancel);
      this.viewport.addEventListener('pointerenter', this._onPointerEnter);
      this.viewport.addEventListener('pointerleave', this._onPointerLeave);
      this.viewport.addEventListener('click', this._onClickCapture, true);
      this.viewport.addEventListener('dragstart', this._onDragStart);

      // Solo anima cuando la cinta está en pantalla.
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          this._inView = entries.some((entry) => entry.isIntersecting);
          this.updatePlayState();
        });
        this._io.observe(this);
      }

      this.buildAnimation();
    }

    disableJsMotion() {
      if (!this._jsEnabled) return;
      this._jsEnabled = false;
      this.classList.remove('pm-platform-marquee--js', 'pm-platform-marquee--dragging');

      if (this.viewport) {
        this.viewport.removeEventListener('pointerdown', this._onPointerDown);
        this.viewport.removeEventListener('pointermove', this._onPointerMove);
        this.viewport.removeEventListener('pointerup', this._onPointerUp);
        this.viewport.removeEventListener('pointercancel', this._onPointerCancel);
        this.viewport.removeEventListener('pointerenter', this._onPointerEnter);
        this.viewport.removeEventListener('pointerleave', this._onPointerLeave);
        this.viewport.removeEventListener('click', this._onClickCapture, true);
        this.viewport.removeEventListener('dragstart', this._onDragStart);
      }
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      this.stopInertia();
      if (this._anim) {
        this._anim.cancel();
        this._anim = null;
      }
      this._pointer = null;
      this._velocity = 0;
    }

    startInertia() {
      if (this._inertiaId) return;
      this._lastTime = 0;
      this._inertiaId = window.requestAnimationFrame((t) => this.inertiaTick(t));
      this.updatePlayState();
    }

    stopInertia() {
      if (this._inertiaId) {
        window.cancelAnimationFrame(this._inertiaId);
        this._inertiaId = null;
      }
    }

    inertiaTick(time) {
      this._inertiaId = null;
      if (!this._jsEnabled) return;
      const dt = this._lastTime ? Math.min(time - this._lastTime, 64) : 0;
      this._lastTime = time;

      if (Math.abs(this._velocity) > INERTIA_MIN) {
        this._offset = wrapOffset(this._offset + this._velocity * dt, this._groupWidth);
        this._velocity *= Math.pow(INERTIA_DECAY, dt / 16);
        this.seekToOffset(this._offset);
        this._inertiaId = window.requestAnimationFrame((t) => this.inertiaTick(t));
        return;
      }

      this._velocity = 0;
      this.updatePlayState(); // retoma el movimiento continuo desde aquí
    }

    handlePointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.stopInertia();
      this._velocity = 0;
      this._offset = this.currentOffset();
      this._pointer = {
        id: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startOffset: this._offset,
        lastX: e.clientX,
        lastT: e.timeStamp,
        dragging: false,
      };
    }

    handlePointerMove(e) {
      const p = this._pointer;
      if (!p || p.id !== e.pointerId) return;

      const dx = e.clientX - p.startX;
      if (!p.dragging) {
        const dy = e.clientY - p.startY;
        if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
        p.dragging = true;
        this.classList.add('pm-platform-marquee--dragging');
        try { this.viewport.setPointerCapture(e.pointerId); } catch (err) {}
        this.updatePlayState(); // pausa la animación mientras se arrastra
      }

      e.preventDefault();
      this._offset = wrapOffset(p.startOffset + dx, this._groupWidth);
      this.seekToOffset(this._offset);

      const dtm = e.timeStamp - p.lastT;
      if (dtm > 0) {
        const v = (e.clientX - p.lastX) / dtm;
        this._velocity = this._velocity * 0.6 + v * 0.4; // suavizado
      }
      p.lastX = e.clientX;
      p.lastT = e.timeStamp;
    }

    handlePointerUp(e, cancelled) {
      const p = this._pointer;
      if (!p || p.id !== e.pointerId) return;
      this._pointer = null;

      if (p.dragging) {
        this.classList.remove('pm-platform-marquee--dragging');
        try { this.viewport.releasePointerCapture(e.pointerId); } catch (err) {}
        // Si el último movimiento fue hace rato, sin inercia.
        if (e.timeStamp - p.lastT > 80) this._velocity = 0;
        this._velocity = Math.max(-3, Math.min(3, this._velocity));
        if (!cancelled) {
          // Evita que el enlace de la marca se abra al soltar tras arrastrar.
          this._suppressClick = true;
          window.setTimeout(() => { this._suppressClick = false; }, 0);
        }
        if (Math.abs(this._velocity) > INERTIA_MIN) {
          this.startInertia();
          return;
        }
      }
      this._velocity = 0;
      this.updatePlayState();
    }

    /* ---------- Medición y clones (agrupados) ---------- */

    scheduleMeasure() {
      if (this._motionQuery && this._motionQuery.matches) return;
      this.cancelMeasure();
      this._measureTimer = window.setTimeout(() => {
        this._measureTimer = null;
        this.measureAndClone();
      }, this._groupWidth ? MEASURE_DELAY : 0);
    }

    cancelMeasure() {
      if (this._measureTimer) {
        window.clearTimeout(this._measureTimer);
        this._measureTimer = null;
      }
    }

    removeDynamicClones() {
      if (!this.track) return;
      this.track.querySelectorAll('[data-pm-marquee-clone]').forEach((clone) => clone.remove());
    }

    measureAndClone() {
      if (this._motionQuery && this._motionQuery.matches) return;

      const containerWidth = this.getBoundingClientRect().width;
      const groupWidth = this.baseGroup.getBoundingClientRect().width;

      if (containerWidth === 0 || groupWidth === 0) {
        // Sin tamaño real todavía; el ResizeObserver disparará de nuevo en
        // cuanto la instancia o el primer grupo obtengan un tamaño válido.
        return;
      }

      // Nada cambió de verdad: no se toca el DOM ni la animación.
      if (
        Math.abs(groupWidth - this._groupWidth) < 0.5 &&
        Math.abs(containerWidth - this._containerWidth) < 0.5
      ) {
        return;
      }

      const speed = parseFloat(this.dataset.speed) || 80;
      this.style.setProperty('--pm-marquee-distance', `${groupWidth}px`);
      this.style.setProperty('--pm-marquee-duration', `${groupWidth / speed}s`);

      this._groupWidth = groupWidth;
      this._containerWidth = containerWidth;

      this.removeDynamicClones();
      const requiredWidth = containerWidth + groupWidth;
      const baseGroupCount = this.track.querySelectorAll('.pm-platform-marquee__group').length;
      let currentWidth = groupWidth * baseGroupCount;

      if (currentWidth < requiredWidth) {
        const fragment = document.createDocumentFragment();
        while (currentWidth < requiredWidth) {
          const clone = this.baseGroup.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-pm-marquee-clone', '');

          clone.querySelectorAll(INTERACTIVE_SELECTOR).forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });

          stripEditorArtifacts(clone);
          fragment.appendChild(clone);
          currentWidth += groupWidth;
        }
        this.track.appendChild(fragment);
      }

      this.buildAnimation(); // conserva la posición proporcional
    }

    /* ---------- Foco de teclado (sin cambios de lógica) ---------- */

    handleFocusIn(event) {
      if (!this.classList.contains('pm-platform-marquee--pause-focus')) return;
      if (event.target.closest('[aria-hidden="true"]')) return;

      let isKeyboardFocus = true;
      try { isKeyboardFocus = event.target.matches(':focus-visible'); } catch (e) {}
      if (!isKeyboardFocus) return;

      this.classList.add('pm-platform-marquee--focus-active');
      this.updatePlayState();
      this.scheduleFocusAlign(event.target);
    }

    handleFocusOut(event) {
      if (!this.classList.contains('pm-platform-marquee--pause-focus')) return;
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;

      this.classList.remove('pm-platform-marquee--focus-active');

      if (this._focusRafId) {
        window.cancelAnimationFrame(this._focusRafId);
        this._focusRafId = null;
      }
      if (this.viewport) this.viewport.scrollLeft = 0;
      this.updatePlayState();
    }

    scheduleFocusAlign(target) {
      if (this._focusRafId) window.cancelAnimationFrame(this._focusRafId);
      this._focusRafId = window.requestAnimationFrame(() => {
        this._focusRafId = null;
        this.alignFocusedElement(target);
      });
    }

    alignFocusedElement(target) {
      if (!this.viewport || !target || !this.viewport.contains(target)) return;

      const viewportRect = this.viewport.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      if (targetRect.left < viewportRect.left) {
        this.viewport.scrollLeft -= viewportRect.left - targetRect.left;
      } else if (targetRect.right > viewportRect.right) {
        this.viewport.scrollLeft += targetRect.right - viewportRect.right;
      }
    }
  }

  customElements.define('pm-platform-marquee', PmPlatformMarquee);
}
